<template>
  <PremiumMetricWidgetBase :widget="widget" @metricLoaded="handleMetricLoaded">
    <div class="chart-wrapper">
      <apexchart height="100%" width="100%" :options="chartOptions" :series="chartSeries" />
    </div>
  </PremiumMetricWidgetBase>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import PremiumMetricWidgetBase from './PremiumMetricWidgetBase.vue';
import { pieColors, pieTextColors } from '@/utils/chartColors';
import {
  getChartExportSettings,
  getDefaultChartOptions,
  type BaseChartOptions,
} from '@/utils/chartSettings';
import { secToDaysHrMinSec } from '@/utils/formatTimeSpent';
import type { WidgetFilter, WidgetSetting, WidgetObject } from '@/types/Widget';

const props = defineProps<{
  widget: WidgetObject;
  area?: WidgetFilter;
  time?: WidgetFilter;
  zone?: WidgetSetting;
  cluster?: WidgetSetting;
  day?: WidgetSetting;
}>();

const metricData = ref<any>(null);

const handleMetricLoaded = (metric: any) => {
  metricData.value = metric.data?.data;
};

const chartSeries = computed(() => {
  return metricData.value?.zones.map((zone: any) => zone.total_dwelltime);
});

const chartLabels = computed(() => {
  return metricData.value?.zones.map((zone: any) => zone.sensor_name);
});

const defaultChartOptions = getDefaultChartOptions(props.widget.code_name);

const chartOptions = computed<BaseChartOptions>(() => {
  const toolbarBase = getChartExportSettings(
    props.widget.name,
    props.area?.name || '',
    props.time?.name || '',
    props.zone?.title || '',
    props.cluster?.title || '',
    '',
  );

  return {
    ...defaultChartOptions.value,
    chart: {
      ...defaultChartOptions.value.chart,
      type: 'donut',
      id: 'meshh-chart-distributed-dwell',
      toolbar: {
        ...toolbarBase,
        export: {
          ...toolbarBase.export,
          csv: {
            ...toolbarBase.export?.csv,
            valueFormatter: (value: string | number) =>
              secToDaysHrMinSec(Number(value)).text,
          },
        },
      },
    },
  labels: chartLabels.value || [],
  colors: pieColors,
  stroke: {
    width: 0,
  },
  plotOptions: {
    pie: {
      customScale: 1,
      donut: {
        size: '50%',
      },
    },
  },
  dataLabels: {
    dropShadow: {
      enabled: false,
    },
    style: {
      colors: pieTextColors,
      fontSize: '12px',
      fontWeight: 'bold',
    },
  },
  tooltip: {
    y: {
      formatter: (val: number) => {
        return secToDaysHrMinSec(val).text;
      },
    },
  },
  };
});
</script>

<style lang="scss">
.chart-wrapper {
  // overflow: hidden causes tooltips to be cut off, do not add
  width: calc(100% - 1px);

  .apexcharts-canvas {
    max-height: 100%;
    max-width: 100%;
  }
}
</style>
